/**
 * Templates (mapping profiles) list page.
 *
 * Shows a table: name, productType, isDefault chip, updatedAt, edit link.
 * Header bar has a "Create new" link → /profiles/new.
 */

import { Link } from "react-router";
import { listWorkspaces, listProfiles } from "@/lib/api/endpoints";
import { CustomerSwitcher } from "@/components/CustomerSwitcher";
import { readActiveCustomerFromDocument } from "@/lib/mapper/active-customer-cookie";
import { PageError, PageLoading, useLoader } from "@/lib/loader";
import {
  DEFAULT_TEMPLATE_TYPE,
  templateTypeLabel,
} from "@/lib/mapper/template-types";
import { shopifyTarget } from "@/lib/mapper/targets";
import { InlineArrow } from "@/components/icons/InlineArrow";
import { marketplaceIcon } from "@/components/icons/brands";


function formatDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ProfilesPage() {
  const loaded = useLoader(async () => {
    const workspaces = await listWorkspaces();
    // Default to the first workspace when no cookie is set yet.
    const activeCustomerId = readActiveCustomerFromDocument() ?? workspaces[0]?.id ?? null;
    const profiles = activeCustomerId ? await listProfiles({ customerId: activeCustomerId }) : [];
    return { workspaces, activeCustomerId, profiles };
  }, []);
  if (loaded.status === "error") return <PageError error={loaded.error} />;
  if (!loaded.data) return <PageLoading />;
  const { workspaces, activeCustomerId, profiles } = loaded.data;
  const groupedProfiles = new Map<string, typeof profiles>();
  for (const profile of profiles) {
    const groupLabel = shopifyTarget.name;
    groupedProfiles.set(groupLabel, [
      ...(groupedProfiles.get(groupLabel) ?? []),
      profile,
    ]);
  }
  const profileGroups = Array.from(groupedProfiles.entries()).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return (
    <div className="mx-auto max-w-7xl p-5 lg:p-6">
      {/* Header */}
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Templates
          </p>
          <h2 className="text-xl font-semibold text-slate-950 dark:text-slate-100">
            Mapping Templates
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {profiles.length} template{profiles.length === 1 ? "" : "s"},
            reusable column-mapping settings per product type
          </p>
        </div>
        <div className="flex items-center gap-3">
          <CustomerSwitcher workspaces={workspaces} activeId={activeCustomerId} />
          <Link
            to="/profiles/new"
            className="inline-flex w-fit items-center rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 dark:hover:bg-slate-200"
          >
            Create new
          </Link>
        </div>
      </div>

      {profiles.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 dark:border-border bg-white dark:bg-surface p-10 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            No templates yet
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Create a template to define how source columns map to Shopify&apos;s
            product CSV columns.
          </p>
          <Link
            to="/profiles/new"
            className="mt-4 inline-flex rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 dark:hover:bg-slate-200"
          >
            Create now
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {profileGroups.map(([groupLabel, list]) => {
            const GroupIcon = marketplaceIcon("shopify");
            return (
              <section
                key={groupLabel}
                className="overflow-hidden rounded border border-slate-200 dark:border-border bg-white dark:bg-surface"
              >
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 dark:border-border bg-slate-50 dark:bg-background px-4 py-3">
                  <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    <GroupIcon
                      size={16}
                      className="text-slate-600 dark:text-slate-300"
                    />
                    {groupLabel}
                  </h3>
                  <span className="rounded-full bg-slate-200 dark:bg-surface-2 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-400">
                    {list.length} template{list.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-left text-sm">
                    <thead className="bg-slate-100 dark:bg-surface-2 text-xs uppercase text-slate-600 dark:text-slate-400">
                      <tr>
                        <th className="px-4 py-2 font-semibold">Name</th>
                        <th className="px-4 py-2 font-semibold">Type</th>
                        <th className="px-4 py-2 font-semibold">
                          Product type
                        </th>
                        <th className="px-4 py-2 font-semibold">Default</th>
                        <th className="px-4 py-2 font-semibold">Updated</th>
                        <th className="px-4 py-2 text-right font-semibold">
                          Edit
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((p) => {
                        const RowIcon = marketplaceIcon("shopify");
                        return (
                          <tr
                            key={p.id}
                            className="border-t border-slate-100 dark:border-border hover:bg-slate-50 dark:hover:bg-surface-2"
                          >
                            <td className="px-4 py-3">
                              <Link
                                to={`/profiles/${p.id}`}
                                className="font-medium text-slate-900 dark:text-slate-100 hover:underline"
                              >
                                {p.name}
                              </Link>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-surface-2 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                                <RowIcon size={13} />
                                {templateTypeLabel(
                                  p.type ?? DEFAULT_TEMPLATE_TYPE,
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {p.productType}
                            </td>
                            <td className="px-4 py-3">
                              {p.isDefault && (
                                <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-950/50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                                  default
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">
                              {formatDate(p.updatedAt)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Link
                                to={`/profiles/${p.id}`}
                                className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:underline"
                              >
                                Edit <InlineArrow />
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
